import React, { useEffect, useState, useContext, useRef } from "react";
import "./Userlogin.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Popup from "../component/PopUp/Popup";
import LoadingOverlay from "../component/LoadingOverlay/LoadingOverlay";
import { NetworkStatusContext } from "../context/NetworkStatusContext";
import config from "../config";

const Userlogin = () => {
  const [userIdInput, setUserIdInput] = useState("");
  const [userPasswordInput, setUserPasswordInput] = useState("");
  const [randomNumber, setRandomNumber] = useState(null);
  const [userIp, setUserIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();
  const { isOnline } = useContext(NetworkStatusContext);
  const fetchCalledRef = useRef(false);

  /* ---------------- ASCII ENCODING ---------------- */
  const convertToAscii = (input) =>
    input.split("").map((char) => char.charCodeAt(0));

  const processAsciiValues = (asciiArray, randomValue) =>
    asciiArray
      .map((num, i) =>
        i % 2 === 0 ? num + randomValue : num - randomValue
      )
      .join(" ");

  /* ---------------- INIT: IP + RANDOM NUMBER ---------------- */
  const fetchInitData = async () => {
    try {
      setLoading(true);

      const ipRes = await axios.get("https://api.ipify.org?format=json");
      const realIp = ipRes.data.ip;
      setUserIp(realIp);

      const randomRes = await axios.post(
        `${config.BASE_URL}/api/auth/init`,
        { ip: realIp }
      );

      if (randomRes?.data?.randomNumber !== undefined) {
        setRandomNumber(Number(randomRes.data.randomNumber));
      } else {
        throw new Error("Random number missing");
      }
    } catch (err) {
      console.error("Init error:", err);
      setShowPopup(true);
      setMessage(
        "Unable to initialize login. Please check your internet and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    sessionStorage.removeItem("userData");

    if (!fetchCalledRef.current && isOnline) {
      fetchInitData();
      fetchCalledRef.current = true;
    }

    if (!isOnline) {
      setShowPopup(true);
      setMessage("Please connect to the internet and try again.");
    }
  }, [isOnline]);

  /* ---------------- LOGIN SUBMIT ---------------- */
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!userIdInput || !userPasswordInput || randomNumber === null) {
      setShowPopup(true);
      setMessage("Please fill in all fields.");
      return;
    }

    const encodedUserId = processAsciiValues(
      convertToAscii(userIdInput),
      randomNumber
    );
    const encodedPassword = processAsciiValues(
      convertToAscii(userPasswordInput),
      randomNumber
    );

    const hashedCredential = `${encodedUserId} 124 124 124 ${encodedPassword}`;

    try {
      setLoading(true);

      const response = await axios.post(
        `${config.BASE_URL}/api/auth/login`,
        {
          hashedCredential,
          ip: userIp,
          randomNumber,
        }
      );

      const data = response?.data || {};

      if (!data.loginSucess) {
        throw new Error("Invalid credentials");
      }

      /* ---------------- SAVE BACKEND RESPONSE AS-IS ---------------- */
      const userData = {
        userId: data.userId,
        userKey: data.userKey,
        companyName: data.companyName,
        flowdevices: Array.isArray(data.flowdevices) ? data.flowdevices : [],
        packingdevices: Array.isArray(data.packingdevices)
          ? data.packingdevices
          : [],
        loginSucess: data.loginSucess,
        newUser: data.newUser,
      };

      sessionStorage.setItem("userData", JSON.stringify(userData));

      /* ---------------- NEW USER FLOW ---------------- */
      if (data.newUser) {
        sessionStorage.setItem("oldUserId", userIdInput);
        sessionStorage.setItem("oldUserPassword", userPasswordInput);
        navigate("/newuserlogin");
      } else {
        navigate("/userdashboard");
      }
    } catch (error) {
      console.error("Login error:", error);

      let errMsg = "Something went wrong. Please try again.";

      if (error.response?.status === 400 || error.response?.status === 401) {
        errMsg = "Incorrect User ID or Password.";
      } else if (error.request) {
        errMsg = "Unable to reach server. Please check your internet.";
      }

      setMessage(errMsg);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminClick = () => {
    window.location.href = "/admin-login";
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container">
      {showPopup && (
        <Popup
          title="Warning!"
          message={message}
          type="failure"
          onClose={() => setShowPopup(false)}
        />
      )}

      {loading && (
        <>
          <LoadingOverlay />
          <div className="loading-message">Loading, please wait...</div>
        </>
      )}

      <div className="left-section">
        <div className="login-card">
          <h1 className="company-name">DataMillr</h1>

          <form onSubmit={handleLoginSubmit}>
            <label>User ID</label>
            <input
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              placeholder="Enter your user ID"
              autoComplete="username"
            />

            <label>Password</label>
            <input
              type="password"
              value={userPasswordInput}
              onChange={(e) => setUserPasswordInput(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
            />

            <div className="button-row2">
              <button type="submit" disabled={loading || randomNumber === null}>
                {loading ? "Logging in..." : "Log In"}
              </button>

              <button
                type="button"
                className="admin-button"
                onClick={handleAdminClick}
              >
                Admin
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="right-section">
        <div className="welcome-text">Welcome Back!</div>
      </div>
    </div>
  );
};

export default Userlogin;
